import { ImageColorFinderFlight, VideoColorFinderFlight } from "@nova/protobuf"
import {
    isV1ColorFinderImageFlight,
    isV1ColorFinderVideoFlight,
    isVideoFlight,
} from "./ColorFinderQueries"
import { colorIndexFromUint8, rgbIndexToRgbV2 } from "./filter/colorspace"
import { cumulativeRgbPlotData } from "./filter/colorplot"
import {
    createCumulativeRgb,
    CumulativeRGB,
} from "@nova/component-color-finder-legend/src/web-workers/createCumulativeRgb/createCumulativeRgb"
import { fetchLegacyColorFinderIndex } from "./remote/ColorFinderRemotes"
import { useSWRNoRevalidate } from "@nova/swr"
import { localVideoMap } from "plugins/shared/useUniversalControl"
import { localImageColorDataMap } from "../ImageColorFinder/ImageColorFinderSlice"
import { uint8ToUint32Array } from "tools/util-buffers"

const getCumulativeRGB = async (
    flight: ImageColorFinderFlight | VideoColorFinderFlight
): Promise<CumulativeRGB | undefined> => {
    if (isVideoFlight(flight)) {
        const localCumulative = localVideoMap[flight.id]?.cumulativeRgb
        if (localCumulative) return localCumulative

        if (isV1ColorFinderVideoFlight(flight)) {
            const isDemo = !!flight.demoId
            const rawPath = flight.job?.colordataPath ?? ""
            const idx = rawPath.indexOf("flight/")
            const colordataKey = isDemo
                ? `public/sample-data/${idx >= 0 ? rawPath.slice(idx) : rawPath}`
                : rawPath
            return await createCumulativeRgb({
                colorIndex: await fetchLegacyColorFinderIndex(colordataKey, isDemo),
            })
        } else {
            const cumulative = flight.job?.cumulativeColorIndex?.slice()
            if (!cumulative) return
            const safeCumulative =
                cumulative instanceof Uint8Array ? cumulative : new Uint8Array(cumulative)
            const u32Cumulative = uint8ToUint32Array(safeCumulative)
            const normalize = rgbIndexToRgbV2(u32Cumulative)
            const colorplotData = cumulativeRgbPlotData(normalize)
            return { ...colorplotData, cumulativeIndex: u32Cumulative }
        }
    } else {
        const localCumulative = localImageColorDataMap[flight.id]?.cumulativeRgb
        if (localCumulative) return localCumulative

        if (isV1ColorFinderImageFlight(flight)) {
            const colorData = flight.images.map((image) => image.colorData)
            const index = colorIndexFromUint8(...colorData)
            return await createCumulativeRgb({
                colorIndex: index,
            })
        } else {
            const cumulative = flight.cumulativeColorIndex
            if (!cumulative) return
            const u32Cumulative = uint8ToUint32Array(cumulative)
            const normalize = rgbIndexToRgbV2(u32Cumulative)
            const colorplotData = cumulativeRgbPlotData(normalize)
            return { ...colorplotData, cumulativeIndex: u32Cumulative }
        }
    }
}

const useCumulativeRGB = (flight: ImageColorFinderFlight | VideoColorFinderFlight) => {
    const { data, isLoading, error } = useSWRNoRevalidate(["cumulative", flight.id], () =>
        getCumulativeRGB(flight)
    )

    if (error) {
        console.error(error)
    }

    return { data, isLoading }
}

export { useCumulativeRGB }
